import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { apiRequest, ApiError } from '../../utils/apiClient';
import { formatAmount } from '../../utils/numberFormat';
import { GooeyLoader } from '../../components/ui/gooey-loader';

type ListingStatus = 'active' | 'pending' | 'sold' | 'rented' | 'inactive';

interface PropertyListing {
  id: string;
  title: string;
  titleAr?: string;
  price: number;
  pricePeriod?: string;
  listingType: string;
  listingStatus: ListingStatus;
  type: string;
  category: string;
  rooms?: number;
  bathrooms?: number;
  areaSqm?: number;
  city?: { nameEn: string; nameAr: string };
  neighborhood?: { nameEn: string; nameAr: string };
  images?: Array<{ url: string }>;
  createdAt: string;
  updatedAt: string;
  // Drives the "Me" (companyId null) vs "{Company name}" badge on each card.
  companyId?: string | null;
  companyName?: string | null;
  companyNameAr?: string | null;
}

const STATUS_OPTIONS: { value: ListingStatus; labelEn: string; labelAr: string; color: string }[] = [
  { value: 'active', labelEn: 'Active', labelAr: 'نشط', color: 'bg-green-100 text-green-700' },
  { value: 'pending', labelEn: 'Pending', labelAr: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'sold', labelEn: 'Sold', labelAr: 'مباع', color: 'bg-blue-100 text-blue-700' },
  { value: 'rented', labelEn: 'Rented', labelAr: 'مؤجر', color: 'bg-[var(--pv-color-surface-sunken)] text-[#003930]' },
  { value: 'inactive', labelEn: 'Inactive', labelAr: 'غير نشط', color: 'bg-gray-100 text-gray-700' },
];

const PAGE_SIZE = 24;

export default function MyListingsPage() {
  const { i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const {
    isSubscribed,
    loading: subscriptionLoading,
    needsContactCompanyAdmin,
    personalListingsCount,
  } = useSubscription();
  const isRTL = i18n.dir() === 'rtl';

  const [listings, setListings] = useState<PropertyListing[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!user && !subscriptionLoading) {
      navigate('/signin?redirect=/listings');
    }
  }, [user, subscriptionLoading, navigate]);

  // Fetch user's listings
  const fetchListings = useCallback(async () => {
    if (!session?.token) return;

    setLoading(true);
    setError(null);

    try {
      // API returns { data: [...], meta: {...} } for list endpoints
      interface ApiPropertyResponse {
        id: string;
        title: string;
        titleAr?: string;
        price: number;
        rentPeriod?: string;
        listingType?: string;
        status?: string;
        propertyType?: string;
        bedrooms?: number;
        bathrooms?: number;
        sqft?: number;
        city?: string;
        district?: string;
        imageUrl?: string;
        galleryImages?: string[];
        dateAdded?: string;
        lastUpdated?: string;
        // From propertyStore.toPropertyResponse — companyId is NULL for
        // personal listings; populated companyName/companyNameAr feeds
        // the "Me" / "{Company}" badge on each card.
        companyId?: string | null;
        companyName?: string | null;
        companyNameAr?: string | null;
      }

      interface ListMeta { page: number; pageSize: number; totalItems: number; totalPages: number }
      const response = await apiRequest<{ data: ApiPropertyResponse[]; meta?: ListMeta } | ApiPropertyResponse[]>(
        `/properties?mine=true&page=${page}&pageSize=${PAGE_SIZE}`,
        { token: session.token }
      );

      // Handle both array response and { data: [...] } response formats
      if (Array.isArray(response)) {
        // Direct array - map to PropertyListing format
        const mapped: PropertyListing[] = response.map((item) => ({
          id: String(item.id),
          title: String(item.title || ''),
          titleAr: item.titleAr,
          price: Number(item.price) || 0,
          pricePeriod: item.rentPeriod,
          listingType: (String(item.listingType || 'RENT')).toLowerCase(),
          listingStatus: (String(item.status || 'active')).toLowerCase() as ListingStatus,
          type: String(item.propertyType || 'apartment'),
          category: String(item.propertyType || 'apartment'),
          rooms: Number(item.bedrooms) || 0,
          bathrooms: Number(item.bathrooms) || 0,
          areaSqm: item.sqft ? Math.round(Number(item.sqft) * 0.0929) : undefined,
          city: item.city ? { nameEn: String(item.city), nameAr: String(item.city) } : undefined,
          neighborhood: item.district ? { nameEn: String(item.district), nameAr: String(item.district) } : undefined,
          images: item.imageUrl ? [{ url: String(item.imageUrl) }] :
                  (item.galleryImages && item.galleryImages.length > 0)
                    ? item.galleryImages.map((url) => ({ url }))
                    : undefined,
          createdAt: String(item.dateAdded || new Date().toISOString()),
          updatedAt: String(item.lastUpdated || new Date().toISOString()),
          companyId: item.companyId ?? null,
          companyName: item.companyName ?? null,
          companyNameAr: item.companyNameAr ?? null,
        }));
        setListings(mapped);
        setTotalItems(mapped.length);
        setTotalPages(1);
      } else if (response && 'data' in response && Array.isArray(response.data)) {
        // Wrapped response { data: [...] } - map to PropertyListing format
        const mapped: PropertyListing[] = response.data.map((item) => ({
          id: String(item.id),
          title: String(item.title || ''),
          titleAr: item.titleAr,
          price: Number(item.price) || 0,
          pricePeriod: item.rentPeriod,
          listingType: (String(item.listingType || 'RENT')).toLowerCase(),
          listingStatus: (String(item.status || 'active')).toLowerCase() as ListingStatus,
          type: String(item.propertyType || 'apartment'),
          category: String(item.propertyType || 'apartment'),
          rooms: Number(item.bedrooms) || 0,
          bathrooms: Number(item.bathrooms) || 0,
          areaSqm: item.sqft ? Math.round(Number(item.sqft) * 0.0929) : undefined,
          city: item.city ? { nameEn: String(item.city), nameAr: String(item.city) } : undefined,
          neighborhood: item.district ? { nameEn: String(item.district), nameAr: String(item.district) } : undefined,
          images: item.imageUrl ? [{ url: String(item.imageUrl) }] :
                  (item.galleryImages && item.galleryImages.length > 0)
                    ? item.galleryImages.map((url) => ({ url }))
                    : undefined,
          createdAt: String(item.dateAdded || new Date().toISOString()),
          updatedAt: String(item.lastUpdated || new Date().toISOString()),
          companyId: item.companyId ?? null,
          companyName: item.companyName ?? null,
          companyNameAr: item.companyNameAr ?? null,
        }));
        setListings(mapped);
        const meta = response.meta;
        setTotalItems(meta?.totalItems ?? mapped.length);
        setTotalPages(meta?.totalPages ?? 1);
      } else {
        setListings([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Failed to fetch listings:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(isRTL ? 'فشل في تحميل العقارات' : 'Failed to load properties');
      }
    } finally {
      setLoading(false);
    }
  }, [session?.token, isRTL, page]);

  useEffect(() => {
    if (user && session?.token) {
      fetchListings();
    }
  }, [user, session?.token, fetchListings]);

  // Update listing status
  const handleUpdateStatus = async (listingId: string, newStatus: ListingStatus) => {
    if (!session?.token) return;

    setUpdatingId(listingId);
    try {
      await apiRequest(`/properties/${listingId}`, {
        method: 'PATCH',
        body: { listing_status: newStatus },
        token: session.token,
      });

      // Update local state
      setListings(prev =>
        prev.map(listing =>
          listing.id === listingId
            ? { ...listing, listingStatus: newStatus }
            : listing
        )
      );
      setShowStatusModal(null);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert(isRTL ? 'فشل في تحديث الحالة' : 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  // Delete listing
  const handleDelete = async (listingId: string) => {
    if (!session?.token) return;

    setDeletingId(listingId);
    try {
      await apiRequest(`/properties/${listingId}`, {
        method: 'DELETE',
        token: session.token,
      });

      // Remove from local state
      setListings(prev => prev.filter(listing => listing.id !== listingId));
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Failed to delete listing:', err);
      alert(isRTL ? 'فشل في حذف العقار' : 'Failed to delete property');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusLabel = (status: ListingStatus) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option ? (isRTL ? option.labelAr : option.labelEn) : status;
  };

  const getStatusColor = (status: ListingStatus) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option?.color || 'bg-gray-100 text-gray-700';
  };

  const formatPrice = (price: number) => {
    return formatAmount(price);
  };

  if (!user) {
    return null;
  }

  // Lock screen: user has personal listings (they own properties as
  // companyId=NULL rows) but has no active subscription — own or inherited.
  // Their listings were set to SUSPENDED at team-removal time, so they're
  // already hidden from public search. This screen replaces the full
  // listings UI so they can't interact with anything else until they
  // subscribe again.
  const isLocked =
    !subscriptionLoading &&
    !isSubscribed &&
    personalListingsCount > 0;

  if (isLocked) {
    return (
      <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
        <Navigation />
        <main className="pb-20">
          <div className="max-w-xl mx-auto px-4 py-16 sm:py-24">
            <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 sm:p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-lock-2-line text-4xl text-amber-600" aria-hidden="true" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--pv-color-primary)] mb-3">
                {isRTL ? 'الاشتراك مطلوب' : 'Subscription required'}
              </h1>
              <p className="text-gray-600 mb-2">
                {isRTL
                  ? `لديك ${personalListingsCount} عقار${personalListingsCount === 1 ? '' : 'ات'} مخفي${personalListingsCount === 1 ? '' : 'ة'} عن العامة لأنك لا تملك اشتراكاً نشطاً.`
                  : `You have ${personalListingsCount} personal listing${personalListingsCount === 1 ? '' : 's'} hidden from the public because you don't have an active subscription.`}
              </p>
              <p className="text-gray-600 mb-8">
                {needsContactCompanyAdmin
                  ? (isRTL
                      ? 'اشتراك الشركة لا يغطي عقاراتك الشخصية - اشترك بنفسك لإعادة عرضها.'
                      : 'Your company subscription does not cover personal listings - subscribe yourself to restore them.')
                  : (isRTL
                      ? 'اشترك الآن لإعادة عرض عقاراتك على المنصة.'
                      : 'Subscribe now to restore your listings on the platform.')}
              </p>
              <Link to="/membership">
                <Button variant="primary" size="lg" className="w-full sm:w-auto">
                  <i className={`ri-vip-crown-line ${isRTL ? 'ml-2' : 'mr-2'}`} aria-hidden="true" />
                  {isRTL ? 'اشترك الآن' : 'Subscribe now'}
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) {
      setPage(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />

      <main className="pb-20">
        {/* Header */}
        <section className="bg-[#004D40] py-8 sm:py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-3 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                  <i className="ri-building-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                    {isRTL ? 'عقاراتي' : 'My Listings'}
                  </h1>
                  <p className="text-[#DDEEEB]">
                    {isRTL
                      ? `${totalItems} عقار مُعلن`
                      : `${totalItems} properties listed`}
                  </p>
                </div>
              </div>

              {isSubscribed && (
                <Link to="/properties/new">
                  <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <i className={`ri-add-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    {isRTL ? 'إضافة عقار' : 'Add Property'}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          {/* Not Subscribed Warning */}
          {!isSubscribed && !subscriptionLoading && (
            <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-yellow-50 border border-yellow-200 rounded-2xl">
              <div className={`flex items-start gap-3 sm:gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
                  <i className="ri-vip-crown-line text-xl sm:text-2xl text-yellow-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-yellow-800 mb-1">
                    {isRTL ? 'اشترك لإدارة العقارات' : 'Subscribe to Manage Properties'}
                  </h3>
                  <p className="text-yellow-700 mb-4">
                    {isRTL
                      ? 'تحتاج إلى اشتراك نشط لإضافة وإدارة العقارات'
                      : 'You need an active subscription to add and manage properties'}
                  </p>
                  <Link to="/membership">
                    <Button variant="primary" size="sm">
                      {isRTL ? 'عرض خطط الاشتراك' : 'View Subscription Plans'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
                <p className="text-gray-500">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-50 rounded-2xl border border-red-200">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-4xl text-red-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-red-800 mb-2">{error}</h3>
              <Button variant="outline" onClick={fetchListings}>
                {isRTL ? 'إعادة المحاولة' : 'Try Again'}
              </Button>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
                <i className="ri-building-line text-4xl text-[#1B7566]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isRTL ? 'لا توجد عقارات بعد' : 'No properties yet'}
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {isRTL
                  ? 'ابدأ بإضافة عقارك الأول للوصول إلى آلاف المشترين والمستأجرين'
                  : 'Start by adding your first property to reach thousands of buyers and renters'}
              </p>
              {isSubscribed && (
                <Link to="/properties/new">
                  <Button variant="primary">
                    <i className={`ri-add-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    {isRTL ? 'إضافة عقار' : 'Add Property'}
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            /* Listings Grid */
            <div className="grid gap-6">
              {listings.map((listing) => {
                const cityName = isRTL ? listing.city?.nameAr : listing.city?.nameEn;
                const neighborhoodName = isRTL ? listing.neighborhood?.nameAr : listing.neighborhood?.nameEn;
                const title = isRTL && listing.titleAr ? listing.titleAr : listing.title;
                const imageUrl = listing.images?.[0]?.url;

                return (
                  <div
                    key={listing.id}
                    className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className={`flex flex-col md:flex-row ${isRTL ? 'md:flex-row-reverse' : ''}`}>
                      {/* Image */}
                      <div className="relative w-full md:w-64 h-40 sm:h-48 md:h-auto bg-gray-100 shrink-0">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-home-line text-4xl text-gray-300"></i>
                          </div>
                        )}

                        {/* Status Badge */}
                        <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'}`}>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(listing.listingStatus)}`}>
                            {getStatusLabel(listing.listingStatus)}
                          </span>
                        </div>

                        {/* Listing Type Badge */}
                        <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'}`}>
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-gray-700">
                            {listing.listingType === 'sale'
                              ? (isRTL ? 'للبيع' : 'For Sale')
                              : (isRTL ? 'للإيجار' : 'For Rent')}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4 sm:p-5">
                        <div className={`flex flex-col h-full ${isRTL ? 'text-right' : ''}`}>
                          {/* Title & Price */}
                          <div className={`flex items-start justify-between gap-3 sm:gap-4 mb-3 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                                  {title}
                                </h3>
                                {/* Ownership chip — "Me" for personal listings
                                    (companyId NULL) or the company name in
                                    teal when the listing is company-owned.
                                    Lets brokers who are on a team see at a
                                    glance which listings stay with them on
                                    company exit vs. which transfer to the
                                    company. */}
                                {listing.companyId ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 text-teal-700"
                                    title={isRTL
                                      ? 'مُدرج تحت الشركة - يبقى مع الشركة إذا غادرت'
                                      : "Listed under the company - stays with the company if you leave"}
                                  >
                                    <i className="ri-building-2-line text-xs" />
                                    <span className="truncate max-w-[140px]">
                                      {isRTL
                                        ? (listing.companyNameAr || listing.companyName || 'Company')
                                        : (listing.companyName || listing.companyNameAr || 'Company')}
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700"
                                    title={isRTL
                                      ? 'إعلان شخصي - يبقى معك'
                                      : 'Personal listing - stays with you'}
                                  >
                                    <i className="ri-user-line text-xs" />
                                    {isRTL ? 'باسمي' : 'Me'}
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm text-gray-500 flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <i className="ri-map-pin-line text-[#004D40]"></i>
                                {cityName}{neighborhoodName ? `, ${neighborhoodName}` : ''}
                              </p>
                            </div>
                            <div className={`flex items-center gap-1.5 text-[#004D40] font-bold ${isRTL ? 'flex-row-reverse' : ''}`}>
                              {!listing.price || listing.price <= 0 ? (
                                <span>{isRTL ? 'السعر عند الطلب' : 'Price on request'}</span>
                              ) : (
                                <>
                                  <SaudiRiyalSymbol className="w-4 h-4" />
                                  <span>{formatPrice(listing.price)}</span>
                                  {listing.listingType === 'rent' && listing.pricePeriod && (
                                    <span className="text-xs font-normal text-gray-500">
                                      / {listing.pricePeriod === 'monthly' ? (isRTL ? 'شهري' : 'mo') : (isRTL ? 'سنوي' : 'yr')}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Property Stats */}
                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            {listing.rooms !== undefined && (
                              <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <i className="ri-hotel-bed-line"></i>
                                {listing.rooms} {isRTL ? 'غرف' : 'Beds'}
                              </span>
                            )}
                            {listing.bathrooms !== undefined && (
                              <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <i className="ri-drop-line"></i>
                                {listing.bathrooms} {isRTL ? (listing.bathrooms === 1 ? 'دورة مياه' : 'دورات مياه') : 'Baths'}
                              </span>
                            )}
                            {listing.areaSqm !== undefined && (
                              <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <i className="ri-ruler-line"></i>
                                {Math.round(listing.areaSqm)} {isRTL ? 'م²' : 'sqm'}
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className={`flex flex-wrap items-center gap-2 mt-auto pt-4 border-t border-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Link to={`/property/${listing.id}`} className="flex-1">
                              <Button variant="outline" size="sm" className="w-full">
                                <i className={`ri-eye-line ${isRTL ? 'ml-1' : 'mr-1'}`}></i>
                                {isRTL ? 'عرض' : 'View'}
                              </Button>
                            </Link>

                            <Link to={`/property/${listing.id}/edit`}>
                              <Button variant="outline" size="sm" className="text-[#004D40] border-[var(--pv-border-soft)] hover:bg-[var(--pv-color-surface-sunken)]">
                                <i className={`ri-pencil-line ${isRTL ? 'ml-1' : 'mr-1'}`}></i>
                                {isRTL ? 'تعديل' : 'Edit'}
                              </Button>
                            </Link>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowStatusModal(listing.id)}
                              disabled={updatingId === listing.id}
                            >
                              {updatingId === listing.id ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <>
                                  <i className={`ri-flag-line ${isRTL ? 'ml-1' : 'mr-1'}`}></i>
                                  {isRTL ? 'الحالة' : 'Status'}
                                </>
                              )}
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteModal(listing.id)}
                              className="text-red-500 border-red-200 hover:bg-red-50"
                              disabled={deletingId === listing.id}
                            >
                              {deletingId === listing.id ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <i className="ri-delete-bin-line"></i>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className={`mt-8 flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"
              >
                {isRTL ? 'السابق' : 'Prev'}
              </button>

              {(() => {
                const nums: number[] = [];
                const start = Math.max(1, page - 2);
                const end = Math.min(totalPages, page + 2);
                for (let i = start; i <= end; i++) nums.push(i);
                return nums.map((n) => (
                  <button
                    key={n}
                    onClick={() => goToPage(n)}
                    className={`min-w-[2.5rem] px-3 py-2 rounded-lg border text-sm font-medium ${
                      n === page
                        ? 'bg-[#004D40] border-[#004D40] text-white'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ));
              })()}

              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"
              >
                {isRTL ? 'التالي' : 'Next'}
              </button>

              <span className={`text-sm text-gray-500 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {isRTL ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}
              </span>
            </div>
          )}
        </div>
      </main>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-6 shadow-2xl" dir={i18n.dir()}>
            <div className={`flex items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-xl font-bold text-gray-900">
                {isRTL ? 'تحديث حالة العقار' : 'Update Property Status'}
              </h3>
              <button
                onClick={() => setShowStatusModal(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-2">
              {STATUS_OPTIONS.map((option) => {
                const currentListing = listings.find(l => l.id === showStatusModal);
                const isCurrentStatus = currentListing?.listingStatus === option.value;

                return (
                  <button
                    key={option.value}
                    onClick={() => handleUpdateStatus(showStatusModal, option.value)}
                    disabled={updatingId === showStatusModal || isCurrentStatus}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isCurrentStatus
                        ? 'bg-[var(--pv-color-surface-sunken)] border-2 border-[#004D40]'
                        : 'bg-gray-50 border-2 border-transparent hover:border-[var(--pv-color-accent-soft)]'
                    } ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${option.color}`}>
                        {isRTL ? option.labelAr : option.labelEn}
                      </span>
                    </div>
                    {isCurrentStatus && (
                      <i className="ri-check-line text-[#004D40] text-lg"></i>
                    )}
                  </button>
                );
              })}
            </div>

            <div className={`flex gap-3 mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="outline"
                onClick={() => setShowStatusModal(null)}
                className="flex-1"
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4 sm:p-6 shadow-2xl" dir={i18n.dir()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-delete-bin-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isRTL ? 'حذف العقار؟' : 'Delete Property?'}
              </h3>
              <p className="text-gray-500">
                {isRTL
                  ? 'هذا الإجراء لا يمكن التراجع عنه. سيتم حذف العقار نهائياً.'
                  : 'This action cannot be undone. The property will be permanently deleted.'}
              </p>
            </div>

            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(null)}
                className="flex-1"
                disabled={deletingId === showDeleteModal}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleDelete(showDeleteModal)}
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={deletingId === showDeleteModal}
              >
                {deletingId === showDeleteModal ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  isRTL ? 'حذف' : 'Delete'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
